if (!customElements.get('lumistar-navigation')) {
  customElements.define(
    'lumistar-navigation',
    class LumistarNavigation extends HTMLElement {
      connectedCallback() {
        if (this.abortController) return;

        this.abortController = new AbortController();
        const { signal } = this.abortController;
        this.wrapper = this.closest('.lumistar-navigation-header') || this.closest('.header-wrapper');
        this.header = this.closest('header') || this.wrapper;
        this.section = this.wrapper?.closest('.section-header');
        this.storeToggle = this.querySelector('[data-ln-store-toggle]');
        this.storePanel = this.querySelector('[data-ln-panel]');
        this.mobileToggle = this.querySelector('[data-ln-mobile-toggle]');
        this.mobilePanel = this.querySelector('[data-ln-mobile-panel]');
        this.tabs = Array.from(this.querySelectorAll('[data-ln-tab]'));
        this.products = Array.from(this.querySelectorAll('.ln-products[data-ln-category]'));
        this.secondaryMenus = Array.from(this.querySelectorAll('details.ln-secondary'));
        this.desktopMedia = window.matchMedia('(min-width: 990px)');
        this.storeOpen = false;
        this.mobileOpen = false;
        this.secondaryOpenSources = new WeakMap();
        this.measureFrame = null;
        this.lastGeometry = null;
        this.scrollHidden = false;
        this.resetScrollTracking();
        this.section?.classList.add('ln-header-managed');

        this.addEventListener('click', this.onClick.bind(this), { signal });
        this.addEventListener('keydown', this.onKeyDown.bind(this), { signal });
        this.addEventListener('focusout', this.onFocusOut.bind(this), { signal });
        document.addEventListener('pointerdown', this.onOutsidePointer.bind(this), { signal });
        document.addEventListener('click', this.onHeaderAction.bind(this), { signal, capture: true });
        document.addEventListener('keydown', this.onDocumentKeyDown.bind(this), { signal });
        document.addEventListener('focusin', this.onDocumentFocus.bind(this), { signal });
        document.addEventListener('shopify:block:select', this.onBlockSelect.bind(this), { signal });
        window.addEventListener('scroll', this.scheduleMeasure.bind(this), { signal, passive: true });
        window.addEventListener('resize', this.onResize.bind(this), { signal, passive: true });
        window.addEventListener('pageshow', this.onResize.bind(this), { signal });
        this.desktopMedia.addEventListener('change', this.onViewportChange.bind(this), { signal });

        [this.storeToggle, this.storePanel].filter(Boolean).forEach((element) => {
          element.addEventListener('pointerenter', (event) => {
            if (!this.isMouse(event) || !this.desktopMedia.matches) return;
            this.clearCloseTimer();
            this.openStore('hover');
          }, { signal });
          element.addEventListener('pointerleave', (event) => {
            if (this.isMouse(event)) this.scheduleStoreClose();
          }, { signal });
        });

        this.tabs.forEach((tab) => {
          tab.addEventListener('pointerenter', (event) => {
            if (this.isMouse(event) && this.desktopMedia.matches) this.selectCategory(tab.dataset.lnTab);
          }, { signal });
        });

        this.secondaryMenus.forEach((details) => {
          details.addEventListener('pointerenter', (event) => {
            if (!this.isMouse(event) || !this.desktopMedia.matches) return;
            this.clearCloseTimer();
            this.openSecondary(details, 'hover');
          }, { signal });
          details.addEventListener('pointerleave', (event) => {
            if (!this.isMouse(event)) return;
            this.clearCloseTimer();
            this.closeTimer = window.setTimeout(() => {
              if (!details.matches(':hover') && !details.contains(document.activeElement)) this.closeSecondary(details);
              this.syncWrapper();
            }, 180);
          }, { signal });
        });

        const initialTab = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];
        if (initialTab) this.selectCategory(initialTab.dataset.lnTab);
        this.closeAll();

        if ('ResizeObserver' in window && this.header) {
          this.resizeObserver = new ResizeObserver(() => this.scheduleMeasure());
          this.resizeObserver.observe(this.header);
        }
        this.measure();
      }

      disconnectedCallback() {
        this.closeAll();
        this.abortController?.abort();
        this.abortController = null;
        this.resizeObserver?.disconnect();
        this.resizeObserver = null;
        this.clearCloseTimer();
        window.clearTimeout(this.focusOutTimer);
        window.cancelAnimationFrame(this.measureFrame);
        this.measureFrame = null;
        this.section?.classList.remove('ln-header-managed', 'ln-header-hidden');
      }

      isMouse(event) {
        return event.pointerType === 'mouse';
      }

      onClick(event) {
        const storeToggle = event.target.closest('[data-ln-store-toggle]');
        if (storeToggle && this.contains(storeToggle)) {
          event.preventDefault();
          const shouldClose = this.storeOpen && (event.detail === 0 || this.storeOpenSource !== 'hover');
          shouldClose ? this.closeStore() : this.openStore(event.detail === 0 ? 'keyboard' : 'click');
          return;
        }

        const tab = event.target.closest('[data-ln-tab]');
        if (tab && this.contains(tab)) {
          event.preventDefault();
          this.selectCategory(tab.dataset.lnTab);
          return;
        }

        const mobileToggle = event.target.closest('[data-ln-mobile-toggle]');
        if (mobileToggle && this.contains(mobileToggle)) {
          event.preventDefault();
          this.mobileOpen ? this.closeMobile(true) : this.openMobile();
          return;
        }

        if (event.target.closest('[data-ln-mobile-close]')) {
          event.preventDefault();
          this.closeMobile(true);
          return;
        }

        const summary = event.target.closest('details.ln-secondary > summary');
        if (summary && this.contains(summary)) {
          event.preventDefault();
          const details = summary.parentElement;
          const shouldClose = details.open && (event.detail === 0 || this.secondaryOpenSources.get(details) !== 'hover');
          shouldClose ? this.closeSecondary(details) : this.openSecondary(details, event.detail === 0 ? 'keyboard' : 'click');
          this.syncWrapper();
          return;
        }

        if (this.mobileOpen && event.target.closest('a[href]') && this.mobilePanel?.contains(event.target)) {
          this.closeMobile();
        }
      }

      onKeyDown(event) {
        const tab = event.target.closest('[data-ln-tab]');
        if (tab && this.tabs.includes(tab)) {
          const currentIndex = this.tabs.indexOf(tab);
          let nextIndex;
          if (event.key === 'ArrowDown' || event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % this.tabs.length;
          if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = this.tabs.length - 1;
          if (nextIndex !== undefined) {
            event.preventDefault();
            const nextTab = this.tabs[nextIndex];
            this.selectCategory(nextTab.dataset.lnTab);
            nextTab.focus();
          }
        }

        if (event.target.closest('[data-ln-store-toggle]') && event.key === 'ArrowDown') {
          event.preventDefault();
          this.openStore('keyboard');
          this.tabs.find((item) => item.getAttribute('aria-selected') === 'true')?.focus();
        }
      }

      onDocumentKeyDown(event) {
        if (event.key === 'Escape') {
          if (this.mobileOpen) {
            const localization = event.target.closest('localization-form');
            if (localization && this.mobilePanel?.contains(localization) && localization.querySelector('[aria-expanded="true"]')) return;
            event.preventDefault();
            this.closeMobile(true);
          } else if (this.storeOpen) {
            event.preventDefault();
            this.closeStore(true);
          } else {
            const openMenu = this.secondaryMenus.find((details) => details.open);
            if (!openMenu) return;
            event.preventDefault();
            this.closeSecondary(openMenu);
            openMenu.querySelector('summary')?.focus({ preventScroll: true });
            this.syncWrapper();
          }
        }

        if (event.key !== 'Tab' || !this.mobileOpen) return;
        const focusable = this.mobileFocusables();
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const focused = document.activeElement;
        if (!focusable.includes(focused) || (event.shiftKey && focused === first)) {
          event.preventDefault();
          (event.shiftKey ? last : first).focus();
        } else if (!event.shiftKey && focused === last) {
          event.preventDefault();
          first.focus();
        }
      }

      onDocumentFocus(event) {
        if (this.header?.contains(event.target)) {
          this.setHeaderHidden(false);
          this.resetScrollTracking();
          this.scheduleMeasure();
        }
        if (!this.mobileOpen || this.wrapper?.contains(event.target) || this.mobilePanel?.contains(event.target)) return;
        (this.mobileFocusables()[0] || this.mobileToggle)?.focus();
      }

      mobileFocusables() {
        const scope = this.wrapper || this.header || this;
        return Array.from(scope.querySelectorAll('a[href], button, input, select, textarea, summary, [tabindex]')).filter((element) => {
          if (element.disabled || element.tabIndex < 0 || element.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
          const closedDetails = element.closest('details:not([open])');
          if (closedDetails && element !== closedDetails.querySelector(':scope > summary')) return false;
          return element.getClientRects().length > 0;
        });
      }

      onOutsidePointer(event) {
        if (this.contains(event.target)) return;
        if (this.mobileOpen && this.wrapper?.contains(event.target)) return;
        this.closeAll(this.contains(document.activeElement));
      }

      onHeaderAction(event) {
        if (!this.header?.contains(event.target)) return;
        if (event.target.closest('.header__search, .header__icon--search, #cart-icon-bubble')) this.closeAll(false);
      }

      onFocusOut() {
        window.clearTimeout(this.focusOutTimer);
        this.focusOutTimer = window.setTimeout(() => {
          this.focusOutTimer = null;
          if (!this.isConnected || this.mobileOpen) return;
          if (!this.contains(document.activeElement)) this.closeAll(false);
        }, 0);
      }

      openStore(source = 'programmatic') {
        if (!this.desktopMedia.matches || !this.storePanel || !this.storeToggle) return;
        this.clearCloseTimer();
        this.secondaryMenus.forEach((details) => this.closeSecondary(details));
        if (!this.storeOpen || source !== 'hover') this.storeOpenSource = source;
        this.storeOpen = true;
        this.storePanel.hidden = false;
        this.storeToggle.setAttribute('aria-expanded', 'true');
        this.measure();
        this.syncWrapper();
      }

      closeStore(returnFocus = false) {
        this.clearCloseTimer();
        const wasOpen = this.storeOpen;
        this.storeOpen = false;
        this.storeOpenSource = null;
        if (this.storePanel) this.storePanel.hidden = true;
        this.storeToggle?.setAttribute('aria-expanded', 'false');
        this.syncWrapper();
        if (returnFocus && wasOpen) this.storeToggle?.focus({ preventScroll: true });
      }

      scheduleStoreClose() {
        this.clearCloseTimer();
        this.closeTimer = window.setTimeout(() => {
          if (!this.storeToggle?.matches(':hover') && !this.storePanel?.matches(':hover') && !this.storePanel?.contains(document.activeElement)) this.closeStore();
        }, 180);
      }

      clearCloseTimer() {
        window.clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }

      selectCategory(category) {
        if (!this.tabs.some((tab) => tab.dataset.lnTab === category)) return;
        this.tabs.forEach((tab) => {
          const selected = tab.dataset.lnTab === category;
          tab.setAttribute('aria-selected', String(selected));
          tab.tabIndex = selected ? 0 : -1;
        });
        this.products.forEach((panel) => { panel.hidden = panel.dataset.lnCategory !== category; });
        this.activeCategory = category;
      }

      openSecondary(details, source = 'programmatic') {
        this.closeStore();
        this.secondaryMenus.forEach((item) => {
          if (item !== details) this.closeSecondary(item);
        });
        if (!details.open || source !== 'hover') this.secondaryOpenSources.set(details, source);
        details.open = true;
        details.querySelector('summary')?.setAttribute('aria-expanded', 'true');
        this.syncWrapper();
      }

      closeSecondary(details) {
        details.open = false;
        this.secondaryOpenSources.delete(details);
        details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
      }

      openMobile() {
        if (this.desktopMedia.matches || !this.mobilePanel || !this.mobileToggle) return;
        this.closeStore();
        this.secondaryMenus.forEach((details) => this.closeSecondary(details));
        this.mobileOpen = true;
        this.mobilePanel.hidden = false;
        this.mobileToggle.setAttribute('aria-expanded', 'true');
        if (this.mobileToggle.dataset.closeLabel) this.mobileToggle.setAttribute('aria-label', this.mobileToggle.dataset.closeLabel);
        this.setMobileDialog();
        this.lockScroll();
        this.measure();
        this.syncWrapper();
        const firstItem = this.mobilePanel.querySelector('.ln-mobile-menu a[href], .ln-mobile-menu summary');
        (firstItem || this.mobileToggle).focus({ preventScroll: true });
        const scrollContainer = this.mobilePanel.querySelector('.ln-mobile-scroll');
        if (scrollContainer) scrollContainer.scrollTop = 0;
      }

      closeMobile(returnFocus = false) {
        const wasOpen = this.mobileOpen;
        this.mobileOpen = false;
        if (this.mobilePanel) this.mobilePanel.hidden = true;
        this.mobileToggle?.setAttribute('aria-expanded', 'false');
        if (this.mobileToggle?.dataset.openLabel) this.mobileToggle.setAttribute('aria-label', this.mobileToggle.dataset.openLabel);
        this.restoreMobileDialog();
        this.unlockScroll();
        this.syncWrapper();
        if (returnFocus && wasOpen) this.mobileToggle?.focus({ preventScroll: true });
      }

      lockScroll() {
        if (this.scrollState) return;
        const body = document.body;
        const documentElement = document.documentElement;
        const scrollbarWidth = window.innerWidth - documentElement.clientWidth;
        this.scrollState = {
          overflow: body.style.overflow,
          paddingRight: body.style.paddingRight,
          position: body.style.position,
          top: body.style.top,
          left: body.style.left,
          width: body.style.width,
          documentOverflow: documentElement.style.overflow,
          hadClass: body.classList.contains('ln-scroll-locked'),
          scrollX: window.scrollX || 0,
          scrollY: this.readScrollPosition(),
        };
        if (scrollbarWidth > 0) body.style.paddingRight = `${parseFloat(getComputedStyle(body).paddingRight) + scrollbarWidth}px`;
        body.style.position = 'fixed';
        body.style.top = `${-this.scrollState.scrollY}px`;
        body.style.left = `${-this.scrollState.scrollX}px`;
        body.style.width = '100%';
        body.style.overflow = 'hidden';
        documentElement.style.overflow = 'hidden';
        body.classList.add('ln-scroll-locked');
      }

      setMobileDialog() {
        if (!this.wrapper || this.dialogAttributes) return;
        const attributes = ['role', 'aria-modal', 'aria-label', 'aria-labelledby'];
        this.dialogAttributes = new Map(attributes.map((name) => [name, this.wrapper.getAttribute(name)]));
        this.wrapper.setAttribute('role', 'dialog');
        this.wrapper.setAttribute('aria-modal', 'true');
        const label = this.mobilePanel?.getAttribute('aria-label');
        if (label) {
          this.wrapper.setAttribute('aria-label', label);
          this.wrapper.removeAttribute('aria-labelledby');
        }
      }

      restoreMobileDialog() {
        if (!this.wrapper || !this.dialogAttributes) return;
        this.dialogAttributes.forEach((value, name) => {
          if (value === null) this.wrapper.removeAttribute(name);
          else this.wrapper.setAttribute(name, value);
        });
        this.dialogAttributes = null;
      }

      unlockScroll() {
        if (!this.scrollState) return;
        const state = this.scrollState;
        ['overflow', 'paddingRight', 'position', 'top', 'left', 'width'].forEach((property) => {
          document.body.style[property] = state[property];
        });
        document.documentElement.style.overflow = state.documentOverflow;
        if (!state.hadClass) document.body.classList.remove('ln-scroll-locked');
        this.scrollState = null;
        window.scrollTo({ left: state.scrollX, top: state.scrollY, behavior: 'instant' });
        this.resetScrollTracking();
      }

      closeAll(returnFocus = false) {
        this.closeStore(returnFocus);
        this.closeMobile(returnFocus);
        this.secondaryMenus?.forEach((details) => this.closeSecondary(details));
        this.syncWrapper();
      }

      syncWrapper() {
        this.wrapper?.classList.toggle('is-navigation-open', Boolean(this.storeOpen || this.mobileOpen || this.secondaryMenus?.some((details) => details.open)));
        if (this.isHeaderPinned()) this.setHeaderHidden(false);
        this.resetScrollTracking();
        this.scheduleMeasure();
      }

      readScrollPosition() {
        if (this.scrollState) return this.scrollState.scrollY;
        const scrollingElement = document.scrollingElement || document.documentElement;
        const maximum = Math.max(0, scrollingElement.scrollHeight - window.innerHeight);
        return Math.min(maximum, Math.max(0, window.scrollY || 0));
      }

      resetScrollTracking() {
        this.lastScrollY = this.readScrollPosition();
        this.scrollAnchorY = this.lastScrollY;
        this.scrollDirection = 0;
      }

      isHeaderPinned() {
        const focused = document.activeElement;
        const hasKeyboardFocus = this.header?.contains(focused) && focused?.matches(':focus-visible');
        return Boolean(
          this.storeOpen || this.mobileOpen || this.secondaryMenus?.some((details) => details.open) ||
          this.header?.querySelector('.header__search details[open], details-modal details[open]') ||
          hasKeyboardFocus
        );
      }

      setHeaderHidden(hidden) {
        if (!this.section || hidden === this.scrollHidden) return;
        this.scrollHidden = hidden;
        this.section.classList.toggle('ln-header-hidden', hidden);
        this.scheduleMeasure();
      }

      updateScrollVisibility() {
        const scrollY = this.readScrollPosition();
        this.wrapper?.classList.toggle('is-scrolled', scrollY > 16);
        if (this.wrapper?.dataset.stickyType !== 'on-scroll-up' || scrollY <= 16 || this.isHeaderPinned()) {
          this.setHeaderHidden(false);
          this.resetScrollTracking();
          return;
        }

        const previousY = this.lastScrollY;
        const difference = scrollY - previousY;
        this.lastScrollY = scrollY;
        if (difference === 0) return;

        const direction = Math.sign(difference);
        if (direction !== this.scrollDirection) {
          this.scrollDirection = direction;
          this.scrollAnchorY = previousY;
        }

        const readThreshold = (name, fallback) => {
          const value = Number.parseInt(this.wrapper?.dataset[name], 10);
          return Number.isFinite(value) ? Math.max(0, value) : fallback;
        };
        const isDesktop = this.desktopMedia.matches;
        const hideOffset = readThreshold(isDesktop ? 'hideDesktop' : 'hideMobile', isDesktop ? 100 : 50);
        const revealDelta = readThreshold(isDesktop ? 'revealDesktop' : 'revealMobile', 10);
        const distance = Math.abs(scrollY - this.scrollAnchorY);

        if (direction > 0 && scrollY > hideOffset && distance >= 8) this.setHeaderHidden(true);
        else if (direction < 0 && distance >= revealDelta) this.setHeaderHidden(false);
      }

      scheduleMeasure() {
        if (this.measureFrame) return;
        this.measureFrame = window.requestAnimationFrame(() => {
          this.measureFrame = null;
          this.measure();
        });
      }

      measure() {
        if (!this.header) return;
        const bounds = this.header.getBoundingClientRect();
        const height = Math.round(bounds.height);
        const offset = Math.max(0, Math.round(bounds.top));
        if (height > 0 && `${height}:${offset}` !== this.lastGeometry) {
          this.lastGeometry = `${height}:${offset}`;
          [this, this.wrapper].filter(Boolean).forEach((element) => {
            element.style.setProperty('--ln-header-height', `${height}px`);
            element.style.setProperty('--ln-header-offset', `${offset}px`);
          });
          document.documentElement.style.setProperty('--header-height', `${height}px`);
        }
        this.updateScrollVisibility();
      }

      onResize() {
        this.setHeaderHidden(false);
        this.resetScrollTracking();
        this.scheduleMeasure();
      }

      onViewportChange() {
        const returnFocus = this.contains(document.activeElement);
        this.closeAll();
        this.setHeaderHidden(false);
        this.resetScrollTracking();
        this.scheduleMeasure();
        if (returnFocus) (this.desktopMedia.matches ? this.storeToggle : this.mobileToggle)?.focus({ preventScroll: true });
      }

      onBlockSelect(event) {
        if (!this.contains(event.target)) return;
        const categoryElement = event.target.closest('[data-ln-category], [data-ln-mobile-category]');
        const category = categoryElement?.dataset.lnCategory || categoryElement?.dataset.lnMobileCategory;
        if (!category) return;
        if (this.desktopMedia.matches) {
          this.selectCategory(category);
          this.openStore();
        } else {
          this.openMobile();
          const details = Array.from(this.querySelectorAll('.ln-mobile-category')).find((item) =>
            item.dataset.lnMobileCategory === category || item.dataset.lnCategory === category
          );
          if (details) {
            details.open = true;
            details.querySelector('summary')?.setAttribute('aria-expanded', 'true');
            details.scrollIntoView({ block: 'nearest' });
          }
        }
      }
    }
  );
}
